/**
 * useDataGridMutation
 *
 * Bridges cell edits to Frappe via `frappe.client.set_value`.
 * Provides optimistic React Query cache updates with rollback on failure.
 *
 * Each cell edit sends: { doctype, name, fieldname: { [field]: value } }
 * Success is silent. Failure reverts the cache and shows a toast.
 */

import { useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { frappeMethod } from '@/lib/erp/utils';
import type { CellEdit, CellSaveState } from './types';
import { getWriteAdapter, invalidateAdapterKeys, type WriteAdapterResult } from './writeAdapters';

/** Key for the save state map: "docName::fieldname" */
function cellKey(rowName: string, fieldname: string): string {
  return `${rowName}::${fieldname}`;
}

const IDLE_STATE: CellSaveState = { status: 'idle' };

export interface UseDataGridMutationReturn {
  saveCell: (edit: CellEdit) => Promise<boolean>;
  getCellSaveState: (rowName: string, fieldname: string) => CellSaveState;
  getRowSaveState: (rowName: string) => 'idle' | 'saving' | 'error';
  clearError: (rowName: string, fieldname: string) => void;
}

export function useDataGridMutation(doctype: string): UseDataGridMutationReturn {
  const queryClient = useQueryClient();
  // Use ref for the state map to avoid re-renders on every save state change.
  // A counter state triggers re-renders only when consumers need it.
  const stateMap = useRef(new Map<string, CellSaveState>());
  /** Tracks last-known `modified` timestamp per row for conflict detection */
  const modifiedMap = useRef(new Map<string, string>());
  const [, setTick] = useState(0);
  const forceUpdate = useCallback(() => setTick((t) => t + 1), []);

  const setStateForCell = useCallback(
    (key: string, state: CellSaveState) => {
      if (state.status === 'idle' && !state.conflict) {
        stateMap.current.delete(key);
      } else {
        stateMap.current.set(key, state);
      }
      forceUpdate();
    },
    [forceUpdate],
  );

  /**
   * Optimistically update all matching list query caches.
   * Returns a rollback function.
   */
  const optimisticUpdate = useCallback(
    (name: string, fieldname: string, newValue: unknown): (() => void) => {
      // Snapshot for rollback
      const snapshots: Array<{ queryKey: unknown[]; data: unknown }> = [];

      queryClient.setQueriesData<unknown>(
        { queryKey: ['doctype-list', doctype] },
        (old: unknown) => {
          if (!old) return old;
          snapshots.push({ queryKey: ['doctype-list', doctype], data: structuredClone(old) });

          // Handle FrappeListResponse shape: { data: T[], total, pageLength }
          if (typeof old === 'object' && old !== null && 'data' in old && Array.isArray((old as { data: unknown[] }).data)) {
            const resp = old as { data: Record<string, unknown>[] };
            return {
              ...resp,
              data: resp.data.map((row) =>
                row.name === name ? { ...row, [fieldname]: newValue } : row,
              ),
            };
          }
          // Handle plain array shape
          if (Array.isArray(old)) {
            return (old as Record<string, unknown>[]).map((row) =>
              row.name === name ? { ...row, [fieldname]: newValue } : row,
            );
          }
          return old;
        },
      );

      // Return rollback function
      return () => {
        for (const snap of snapshots) {
          queryClient.setQueriesData(
            { queryKey: snap.queryKey },
            () => snap.data,
          );
        }
      };
    },
    [queryClient, doctype],
  );

  const saveCell = useCallback(
    async (edit: CellEdit): Promise<boolean> => {
      // Skip if value unchanged
      if (edit.oldValue === edit.value) return true;

      const key = cellKey(edit.name, edit.fieldname);
      setStateForCell(key, { status: 'saving' });

      // Optimistic cache update
      const rollback = optimisticUpdate(edit.name, edit.fieldname, edit.value);

      try {
        // Some fields are hydrated from a different table than `tab{DocType}`
        // (e.g. Item.standard_rate is sourced from tabItem Price by
        // list_items_with_variants STEP 6b). For those, route the write
        // through a field-specific adapter so it lands in the same table the
        // read path queries. Otherwise fall back to frappe.client.set_value.
        const adapter = getWriteAdapter(edit.doctype, edit.fieldname);

        let serverModified: string | undefined;
        let adapterResult: WriteAdapterResult | null = null;

        if (adapter) {
          adapterResult = await adapter({
            name: edit.name,
            value: edit.value,
            oldValue: edit.oldValue,
          });
          serverModified = adapterResult.modified;
        } else {
          const result = await frappeMethod<Record<string, unknown>>('frappe.client.set_value', {
            doctype: edit.doctype,
            name: edit.name,
            fieldname: { [edit.fieldname]: edit.value },
          });
          serverModified = (result as Record<string, unknown>)?.modified as string | undefined;
        }

        // Conflict detection: compare modified timestamps
        const knownModified = modifiedMap.current.get(edit.name);
        let isConflict = false;
        if (serverModified) {
          if (knownModified && knownModified !== serverModified) {
            // Another user modified this row — set conflict flag
            isConflict = true;
          }
          modifiedMap.current.set(edit.name, serverModified);
        }

        // Success: clear state or mark conflict
        if (isConflict) {
          setStateForCell(key, { status: 'idle', conflict: true });
        } else {
          setStateForCell(key, IDLE_STATE);
        }

        // Background refetch for eventual consistency
        queryClient.invalidateQueries({ queryKey: ['doctype-list', doctype] });

        // Adapter-specific invalidations (e.g. pricing-summary for price writes)
        if (adapterResult) {
          invalidateAdapterKeys(queryClient, adapterResult);
        }

        return true;
      } catch (err: unknown) {
        // Failure: revert cache and show error
        rollback();
        const message =
          err instanceof Error ? err.message : 'Failed to save. Please try again.';
        setStateForCell(key, { status: 'error', error: message });
        toast.error(`Could not save ${edit.fieldname}: ${message}`);
        return false;
      }
    },
    [doctype, queryClient, setStateForCell, optimisticUpdate],
  );

  const getCellSaveState = useCallback(
    (rowName: string, fieldname: string): CellSaveState => {
      return stateMap.current.get(cellKey(rowName, fieldname)) ?? IDLE_STATE;
    },
    [],
  );

  const getRowSaveState = useCallback(
    (rowName: string): 'idle' | 'saving' | 'error' => {
      const prefix = `${rowName}::`;
      let hasError = false;
      for (const [key, state] of stateMap.current) {
        if (key.startsWith(prefix)) {
          if (state.status === 'saving') return 'saving';
          if (state.status === 'error') hasError = true;
        }
      }
      return hasError ? 'error' : 'idle';
    },
    [],
  );

  const clearError = useCallback(
    (rowName: string, fieldname: string) => {
      setStateForCell(cellKey(rowName, fieldname), IDLE_STATE);
    },
    [setStateForCell],
  );

  return { saveCell, getCellSaveState, getRowSaveState, clearError };
}
